import { parsePhoneNumber } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import { ReactDatePickerProps } from 'react-datepicker';
import { UseControllerProps } from 'react-hook-form';
import { FieldObject } from '../openapi/atlantis';
import {
  GooglePlaceLocationType,
  getAddressFromAddressComponent,
} from './GoogleMapUtils';
import { EMAIL_REGEX } from './RegexUtils';

export type Validation = UseControllerProps['rules'];

export type FieldValidationType = {
  type: string;
  value: string;
};

export const EMAIL_VALIDATIONS: Validation = {
  pattern: {
    value: EMAIL_REGEX,
    message: 'Please enter valid email address',
  },
};

export const USERNAME_VALIDATIONS: Validation = {
  minLength: {
    value: 2,
    message: 'Username must be at least 2 characters',
  },
  maxLength: {
    value: 15,
    message: 'Username must be fewer than 15 characters',
  },
};

export const USERNAME_OR_EMAIL_VALIDATIONS: Validation = {
  required: 'Email or username required',
  validate: (value: string) => {
    if (!value || value.trim().length === 0) {
      return 'Email or username required';
    }

    // If it contains @, validate as email
    if (value.includes('@')) {
      if (!EMAIL_REGEX.test(value)) {
        return 'Please enter valid email address';
      }
    } else {
      if (value.length < 2) {
        return 'Username must be at least 2 characters';
      }
      if (value.length >= 15) {
        return 'Username must be fewer than 15 characters';
      }
    }
    return undefined;
  },
};

export const EMAIL_LOGIN_VALIDATIONS: Validation = {
  required: 'Please enter email address',
  ...EMAIL_VALIDATIONS,
};

// eslint-disable-next-line no-useless-escape
export const SYMBOLS_REGEX = /[!@#$%*()"';:,<>|\\\/=\-_^&]/;

export const PASSWORD_VALIDATIONS: Validation = {
  minLength: {
    value: 12,
    message: 'Password must have a minimum of 12 characters',
  },
  maxLength: {
    value: 128,
    message: 'Password cannot be more than 128 characters',
  },
  required: 'Password is required',
  validate: (value: string) => {
    if (/[\s]/.test(value)) {
      return 'Password cannot contain any spaces';
    }

    const errors = [];
    if (!/[a-z]/.test(value)) {
      errors.push('1 lower-case character');
    }

    if (!/[A-Z]/.test(value)) {
      errors.push('1 upper-case character');
    }

    if (!/[0-9]/.test(value)) {
      errors.push('1 digit');
    }

    if (!SYMBOLS_REGEX.test(value)) {
      errors.push('1 symbol');
    }

    if (errors.length === 0) {
      return undefined;
    }

    return `Password must contain at least: ${errors.join(', ')}`;
  },
};

export const PHONE_NUMBER_VALIDATIONS: Validation = {
  validate: async (phone: string) => {
    if (!phone) return 'Please enter phone number';

    try {
      const isValidPhoneNumber = await parsePhoneNumber(
        `+1${phone}`,
      )?.isValid();
      return isValidPhoneNumber ? true : 'Please enter a valid phone number';
    } catch {
      return 'Please enter a valid phone number';
    }
  },
};

export const NUMERIC_ONLY_VALIDATIONS: Validation = {
  pattern: {
    value: /^\d+$/,
    message: 'Only numeric values are allowed',
  },
};

export const getInputValidations = (field: FieldObject): Validation => {
  let validations: Validation = {};

  if (field.required) {
    validations.required = 'Required';
  }

  for (let validation of field.validations || []) {
    if (validation.type === 'regex') {
      validations.pattern = {
        //@ts-ignore
        value: new RegExp(validation.value),
        //@ts-ignore
        message: validation.message,
      };
    }
  }

  return validations;
};

export const getDatePickerConfig = (field: FieldObject): any => {
  let config: Omit<
    ReactDatePickerProps,
    'name' | 'onChange' | 'selected' | 'onBlur' | 'readOnly'
  > = {};

  field.validations?.forEach((validation: any) => {
    //@ts-ignore
    const date = DateTime.fromISO(validation.effectiveValue);

    if (date.isValid) {
      if (validation.type === 'minDate') {
        config.minDate = date
          .setZone('local', { keepLocalTime: true })
          .toJSDate();
      }

      if (validation.type === 'maxDate') {
        config.maxDate = date
          .setZone('local', { keepLocalTime: true })
          .toJSDate();
      }
    }
  });

  return config;
};

export const getGoogleAutoCompleteInputValidations = (
  field: FieldObject,
): Validation => {
  return {
    validate: (value: GooglePlaceLocationType | undefined) => {
      // Check if the field is required and the value is empty
      if (field.required && !value?.formatted_address) {
        return 'Required';
      }

      if (value?.place_id) {
        // Get the address object from the address components
        const address = getAddressFromAddressComponent(
          value?.address_components!,
        );

        // Get the list of address component validations from the field validations
        const requiredAddressFieldValidation = [
          ...((field?.validations || []) as FieldValidationType[]),
        ].filter((validation) => validation.type === 'addressComponent');

        // Check if any required address component is missing or invalid
        const areFieldValidationsInvalid = requiredAddressFieldValidation
          .filter((v) => address.hasOwnProperty(v.value))
          .some((v) => !address[v.value as keyof typeof address]);

        if (areFieldValidationsInvalid) {
          return 'Please enter a valid address';
        }
      }

      // Return undefined if all validations pass
      return undefined;
    },
  };
};
